/**
 * Registry for the currently open dialog.<id> client subscription.
 *
 * POST /api/dialog-token is only for that open dialog. Persistent operator
 * channels come from POST /api/token JWT `subs`. POST /api/widget/token is
 * visitor-only and must not be used from the admin app.
 *
 * IMPORTANT (centrifuge v5): publications on a *client* Subscription are emitted
 * only on that Subscription — NOT on `centrifuge.on('publication')`. Server-side
 * JWT subs still use the client-level event. Attach a publication handler on the
 * open-dialog client subscription for off-/dialogs sounds.
 *
 * Sound buckets: inbox (unaccepted) vs my (accepted) are classification-only
 * (no bulk client subscribe).
 */

import {
  doesDialogsPageOwnNotifications,
  notifyFromDialogChannelEvent,
} from '@utils/operatorInboxNotifier'
import { unwrapCentrifugePublication } from '@utils/unwrapCentrifugePublication'

type CentrifugeLike = {
  getSubscription?: (channel: string) => any
  getServerSubscription?: (channel: string) => any
  newSubscription: (channel: string, opts?: any) => any
  removeSubscription?: (sub: any) => void
}

type TokenGetter = (channel: string) => Promise<string>
type DialogChannelUiListener = (channel: string, data: unknown) => void

const DIALOG_SUBS_LIMIT = 50
/** Centrifugo 105 already subscribed — channel is already a server-side sub.
 *  103 is permission denied and must NOT be treated as server-side. */
const SERVER_SIDE_SUBSCRIBE_CODES = new Set([105])

let centrifuge: CentrifugeLike | null = null
let getToken: TokenGetter | null = null
/** DialogsPage registers this to receive dialog.* publications for UI (messages/typing). */
let dialogChannelUiListener: DialogChannelUiListener | null = null
/** Classification only (inbox vs my) for off-page inbox-channel sounds. Not client-subs. */
let desiredIds: string[] = []
/** Unaccepted (inbox) dialog IDs — messages here use new_conversation sound. */
let unacceptedDialogIds = new Set<string>()
/**
 * The only dialog.<id> that may hold a client subscribe via POST /api/dialog-token.
 * Connection JWT (`POST /api/token`) covers persistent operator/widget channels.
 */
let openDialogId: string | null = null
/**
 * Dialog IDs that are already covered by connection-JWT server-side subs.
 * Creating a client Subscription for these fails with code 103/105 and must not be retried.
 */
const serverSideOnlyIds = new Set<string>()

type SubEntry = {
  sub: any
  channel: string
  onPublication: (ctx: { data?: unknown }) => void
}

const clientSubs = new Map<string, SubEntry>()

/**
 * While DialogsPage is mounted it owns sounds, but client-sub publications only fire on
 * the Subscription — not on `centrifuge.on('publication')`. Register a UI listener here
 * so typing/messages still reach the page even if its local handler attach races.
 */
export function setDialogChannelUiListener(listener: DialogChannelUiListener | null): void {
  dialogChannelUiListener = listener
}

export function getDialogChannelUiListener(): DialogChannelUiListener | null {
  return dialogChannelUiListener
}

/** Dispatch a dialog.* publication to the DialogsPage UI listener (if registered). */
export function forwardDialogChannelToUi(channel: string, data: unknown): void {
  if (!channel.startsWith('dialog.')) return
  dialogChannelUiListener?.(channel, data)
}

export function markSharedDialogServerSide(dialogId: string): void {
  const id = String(dialogId ?? '').trim()
  if (!id) return
  serverSideOnlyIds.add(id)
}

export function isSharedDialogServerSide(dialogId: string): boolean {
  return serverSideOnlyIds.has(String(dialogId ?? '').trim())
}

/** Allow a new client subscribe after accept (old JWT may have marked the channel server-side). */
export function unmarkSharedDialogServerSide(dialogId: string): void {
  const id = String(dialogId ?? '').trim()
  if (!id) return
  serverSideOnlyIds.delete(id)
}

function normalizeIdList(ids: Iterable<string>, limit?: number): string[] {
  const next: string[] = []
  const seen = new Set<string>()
  for (const raw of ids) {
    const id = String(raw ?? '').trim()
    if (!id || seen.has(id)) continue
    const lower = id.toLowerCase()
    if (lower === 'undefined' || lower === 'null' || lower === 'nan') continue
    seen.add(id)
    next.push(id)
    if (limit != null && next.length >= limit) break
  }
  return next
}

function handleSubPublication(channel: string, ctx: { data?: unknown }): void {
  const data = unwrapCentrifugePublication(ctx?.data)
  if (!data || typeof data !== 'object') return

  // Always forward to DialogsPage UI when registered (typing + chat-history).
  dialogChannelUiListener?.(channel, data)

  // Sounds only when the dialogs page is not handling notifications itself.
  if (doesDialogsPageOwnNotifications()) return

  const chatId = channel.startsWith('dialog.')
    ? (() => {
        const rest = channel.slice('dialog.'.length)
        const dotIdx = rest.indexOf('.')
        return (dotIdx === -1 ? rest : rest.slice(0, dotIdx)).trim()
      })()
    : ''
  const soundBucket = chatId && isUnacceptedSharedDialog(chatId) ? 'unaccepted' : 'accepted'
  notifyFromDialogChannelEvent(channel, data, { soundBucket })
}

export function setSharedDialogChannelContext(options: {
  centrifuge: CentrifugeLike | null
  getToken: TokenGetter | null
}): void {
  centrifuge = options.centrifuge
  getToken = options.getToken
  for (const [id, entry] of clientSubs) {
    let live: unknown = null
    try {
      live = centrifuge?.getSubscription?.(entry.channel) ?? null
    } catch {
      live = null
    }
    if (!centrifuge || live !== entry.sub) {
      try {
        entry.sub.off?.('publication', entry.onPublication)
      } catch {
        /* ignore */
      }
      clientSubs.delete(id)
    }
  }
  if (!centrifuge || !getToken) return
  pruneDialogClientSubsToOpen()
}

/**
 * POST /api/dialog-token is only for the currently open dialog.
 * Switching away unsubscribes the previous channel; the same id is a no-op
 * so we never unsubscribe+subscribe dialog.N and drop in-flight publications.
 */
export function setOpenDialogChannelId(id: string | null): void {
  const next = String(id ?? '').trim() || null
  if (openDialogId !== next) {
    openDialogId = next
    pruneDialogClientSubsToOpen()
  }
  if (next) attachSharedHandlerIfSubExists(next)
}

export function getOpenDialogChannelId(): string | null {
  return openDialogId
}

/** True when this process holds a client Subscription for dialog.<id>. */
export function hasSharedDialogClientSub(dialogId: string): boolean {
  return clientSubs.has(String(dialogId ?? '').trim())
}

/**
 * Replace the set of dialog IDs used only for sound classification.
 * Does not create or drop Centrifugo client subscriptions.
 */
export function setDesiredSharedDialogChannelIds(ids: Iterable<string>): void {
  desiredIds = normalizeIdList(ids, DIALOG_SUBS_LIMIT)
  const desired = new Set(desiredIds)
  unacceptedDialogIds = new Set([...unacceptedDialogIds].filter((id) => desired.has(id)))
}

/**
 * Sound classification only (inbox vs my). Client subscribe stays on the open dialog.
 * Unaccepted (inbox) dialogs play new_conversation; accepted (my) play accepted_dialog.
 */
export function setSharedDialogSoundBuckets(options: {
  unacceptedIds: Iterable<string>
  acceptedIds: Iterable<string>
}): void {
  const unaccepted = normalizeIdList(options.unacceptedIds)
  const accepted = normalizeIdList(options.acceptedIds)
  unacceptedDialogIds = new Set(unaccepted)

  const merged: string[] = []
  const seen = new Set<string>()
  for (const id of [...unaccepted, ...accepted]) {
    if (seen.has(id)) continue
    seen.add(id)
    merged.push(id)
    if (merged.length >= DIALOG_SUBS_LIMIT) break
  }
  desiredIds = merged
}

/** True when this dialog is classified as unaccepted/inbox for off-page sounds. */
export function isUnacceptedSharedDialog(chatId: string): boolean {
  return unacceptedDialogIds.has(String(chatId ?? '').trim())
}

/** True when this dialog is in the accepted/my sound bucket. */
export function isAcceptedSharedDialog(chatId: string): boolean {
  const id = String(chatId ?? '').trim()
  if (!id) return false
  return desiredIds.includes(id) && !unacceptedDialogIds.has(id)
}

/**
 * Mark a dialog as unaccepted/inbox for off-page sound routing (e.g. after an
 * inbox-channel new conversation while the operator is on another page).
 */
export function markSharedDialogUnaccepted(chatId: string): void {
  const id = String(chatId ?? '').trim()
  if (!id) return
  unacceptedDialogIds.add(id)
  if (desiredIds.includes(id)) return
  if (desiredIds.length >= DIALOG_SUBS_LIMIT) return
  desiredIds = [...desiredIds, id]
}

export function clearSharedDialogChannelSubscriptions(): void {
  // Do not clear dialogChannelUiListener — DialogsPage owns that registration.
  // Clearing it here left typing/messages dead after centrifuge re-init.
  openDialogId = null
  for (const [id, entry] of clientSubs) {
    try {
      entry.sub.off?.('publication', entry.onPublication)
      entry.sub.unsubscribe()
      centrifuge?.removeSubscription?.(entry.sub)
    } catch {
      /* ignore */
    }
    clientSubs.delete(id)
  }
  desiredIds = []
  unacceptedDialogIds = new Set()
  // Keep serverSideOnlyIds — JWT server-side channels stay server-side across re-sync.
}

function isChannelServerSideOnClient(channel: string): boolean {
  try {
    if (centrifuge?.getServerSubscription?.(channel)) return true
  } catch {
    /* older builds */
  }
  try {
    // centrifuge@5.0.x keeps JWT server-side channels in a private map.
    const serverSubs = (centrifuge as { _serverSubs?: Record<string, unknown> } | null)?._serverSubs
    if (serverSubs?.[channel] != null) return true
  } catch {
    /* ignore */
  }
  return false
}

function pruneDialogClientSubsToOpen(): void {
  for (const [id, entry] of clientSubs) {
    if (id === openDialogId) continue
    try {
      entry.sub.off?.('publication', entry.onPublication)
      entry.sub.unsubscribe()
      centrifuge?.removeSubscription?.(entry.sub)
    } catch {
      /* ignore */
    }
    clientSubs.delete(id)
  }
}

/** Attach the shared publication handler to a client sub DialogsPage already created. */
function attachSharedHandlerIfSubExists(dialogId: string): void {
  if (!centrifuge || clientSubs.has(dialogId)) return
  const channel = `dialog.${dialogId}`
  let existing: unknown = null
  try {
    existing = centrifuge.getSubscription?.(channel) ?? null
  } catch {
    existing = null
  }
  if (!existing) return
  ensureClientSub(dialogId)
}

function ensureClientSub(dialogId: string): void {
  if (!centrifuge || !getToken) return
  if (serverSideOnlyIds.has(dialogId)) return
  const channel = `dialog.${dialogId}`

  // Server-side JWT subs deliver via centrifuge.on('publication') / join / leave.
  // Do not create a client sub that fails with 103 and is retried on every list sync.
  if (isChannelServerSideOnClient(channel)) {
    serverSideOnlyIds.add(dialogId)
    return
  }

  let sub = centrifuge.getSubscription?.(channel) ?? null
  if (!sub) {
    // Only DialogsPage creates dialog.<id> client subs (token → subscribe → history).
    return
  }

  const onPublication = (ctx: { data?: unknown }) => handleSubPublication(channel, ctx)

  const removeBroken = (code?: number) => {
    try {
      sub.off?.('publication', onPublication)
      centrifuge?.removeSubscription?.(sub)
    } catch {
      /* ignore */
    }
    clientSubs.delete(dialogId)
    if (code != null && SERVER_SIDE_SUBSCRIBE_CODES.has(code)) {
      serverSideOnlyIds.add(dialogId)
    }
  }

  sub.on('publication', onPublication)
  sub.on('error', (ctx: { error?: { code?: number }; code?: number }) => {
    const code = ctx?.error?.code ?? ctx?.code ?? 0
    if (SERVER_SIDE_SUBSCRIBE_CODES.has(code)) removeBroken(code)
  })
  sub.on('unsubscribed', (ctx: { code?: number }) => {
    const code = ctx?.code ?? 0
    if (SERVER_SIDE_SUBSCRIBE_CODES.has(code)) removeBroken(code)
  })

  clientSubs.set(dialogId, { sub, channel, onPublication })
  if (sub.state !== 'subscribed') {
    try {
      sub.subscribe()
    } catch (e) {
      console.warn('[dialogChannelSubs] subscribe failed:', channel, e)
      try {
        sub.off?.('publication', onPublication)
      } catch {
        /* ignore */
      }
      clientSubs.delete(dialogId)
    }
  }
}
